import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { observer } from "mobx-react";
import dataStore from "../../Store/dataStore";
import * as itemService from "../../Services/Http/ItemService";
import * as stockCheckService from "../../Services/Http/StockCheckService";
import { StockCheck } from "../../Models/StockCheck";

//Stock Check: scan an item (barcode, stock code or pallet label) to see its stock levels and price. Read only.

const successSound = new Audio();
successSound.src = "/assets/sounds/success.mp3";
successSound.load();

const errorSound = new Audio();
errorSound.src = "/assets/sounds/error.mp3";
errorSound.load();

const money = (n: number): string => n.toLocaleString(navigator.language, { style: "currency", currency: "AUD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qtyText = (n: number): string => n.toLocaleString(navigator.language, { maximumFractionDigits: 3 });

const errorText = (error: any, fallback: string = "Something went wrong"): string => {
  if (typeof error === "string" && error) {
    return error;
  }
  return (error && typeof error.message === "string" && error.message) || fallback;
};

const StockCheckPage = observer(() => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [item, setItem] = useState<StockCheck | null>(null);
  const searchBoxInputRef = useRef<HTMLInputElement>(null);

  const fail = (message: string, autoClose: number | false = 2500) => {
    setSearchTerm("");
    errorSound.play();
    toast.error(message, { autoClose });
  };

  //------------- scanning -------------
  const onBarcodeScanned = useCallback(
    async (barcode: string): Promise<void> => {
      const code = (barcode || "").trim();
      if (!code || busy) {
        return;
      }
      setBusy(true);
      try {
        //a product barcode, stock code or pallet label - pda_BarcodeScan works out which stock item it is
        const scan = await itemService.scanItemFirstAPI(code, dataStore.serverUrl || "", dataStore.dbKey || "");
        if (!scan || !scan.stockCode) {
          fail(scan?.status === 2 || !scan ? "Barcode not found" : "That is not a stock item");
          return;
        }
        const stock = await stockCheckService.getStockCheckAPI(scan.stockCode, dataStore.serverUrl || "", dataStore.dbKey || "");
        setItem(stock);
        setSearchTerm("");
        successSound.play();
      } catch (error: any) {
        const text = errorText(error);
        fail(/duplicated barcode/i.test(text) ? "That barcode matches more than one item - type the stock code instead" : text, false);
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy],
  );

  //a scanner types into the page; collect it and look it up on Enter / Tab
  const onKeyPressed = useCallback(
    (event: any) => {
      if (event.target && (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement)) {
        return;
      }
      const keyPressed = event.key ? event.key : "";
      if (keyPressed.toLowerCase() === "tab" || keyPressed.toLowerCase() === "enter") {
        event.preventDefault();
        setSearchTerm((oldState) => {
          onBarcodeScanned(oldState);
          return oldState;
        });
      } else if (keyPressed.length === 1) {
        setSearchTerm((oldState) => oldState + keyPressed);
      }
    },
    [onBarcodeScanned],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeyPressed);
    return () => {
      window.removeEventListener("keydown", onKeyPressed);
    };
  }, [onKeyPressed]);

  useEffect(() => {
    if (!busy) {
      searchBoxInputRef.current?.focus();
    }
  }, [busy]);

  const row = (label: string, value: string, bold: boolean = false) => (
    <li className="list-group-item d-flex justify-content-between align-items-center p-2">
      <span className="text-muted">{label}</span>
      <span className={bold ? "fw-bold" : ""} style={{ fontSize: "1.2rem" }}>
        {value}
      </span>
    </li>
  );

  return (
    <div className="container-limited-width">
      <div className="row sticky-top-3 search-bar">
        <div className="col-12 p-2">
          <input
            name="searchBox"
            type="text"
            className="form-control search-box"
            placeholder="Scan or type a stock code / barcode"
            autoCapitalize="none"
            autoComplete="off"
            value={searchTerm}
            disabled={busy}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyUp={(e) => {
              if (e.key && e.key.toLowerCase() === "enter") {
                e.preventDefault();
                onBarcodeScanned(searchTerm);
              }
            }}
            ref={searchBoxInputRef}
          />
        </div>
      </div>
      {!item && <div className="text-muted text-center my-4">Scan an item to check its stock.</div>}
      {item && (
        <div className="card">
          <div className="card-body p-2">
            <div className="fw-bold" style={{ fontSize: "1.2rem" }}>
              {item.stockDesc}
            </div>
          </div>
          <ul className="list-group list-group-flush">
            {row("Stock Code", item.stockCode, true)}
            {row("Barcode", item.stockBarcode || "-")}
            {row("Stock Qty", qtyText(item.stockOnHandQty))}
            {row("Orders", qtyText(item.stockSalesOrderQty))}
            {row("Available", qtyText(item.stockAvailableQty), true)}
            {row(
              item.sellUnit ? `Price / ${item.sellUnit}` : "Price",
              item.price === null || item.price === undefined ? "-" : `${money(item.price)}${item.incGST ? " inc GST" : ""}`,
              true,
            )}
          </ul>
        </div>
      )}
    </div>
  );
});

export default StockCheckPage;
